import { zipRecordPermissions } from './recordPermissions'
import type { FgaComputedRelation } from './generated/model'

/**
 * What a caller may do to one request: every computed relation the model defines on `request`, paired
 * with the camelCase key it is reported under. Wider than the vocabulary a permission set configures:
 * seeing the record is computed from holding any permission rather than granted, and
 * `can_edit_po_number` / `can_manage_followers` are record-level participant carve-outs of
 * `edit_metadata` / `manage_participants` rather than set toggles.
 */
export const REQUEST_RECORD_PERMISSIONS = [
  ['canView', 'can_view'],
  ['canPerformBaseTasks', 'can_perform_base_tasks'],
  ['canPerformAdminTasks', 'can_perform_admin_tasks'],
  ['canEditMetadata', 'can_edit_metadata'],
  ['canEditPoNumber', 'can_edit_po_number'],
  ['canManageParticipants', 'can_manage_participants'],
  ['canManageFollowers', 'can_manage_followers'],
  ['canPause', 'can_pause'],
  ['canReject', 'can_reject'],
  ['canDelete', 'can_delete'],
  ['canEditSubmissions', 'can_edit_submissions'],
  ['canManageDocuments', 'can_manage_documents'],
  ['canManageAssignees', 'can_manage_assignees'],
  ['canManageWorkflow', 'can_manage_workflow'],
  ['canAdminister', 'can_administer'],
] as const satisfies readonly (readonly [string, FgaComputedRelation<'request'>])[]

export type RequestRecordPermissionKey = (typeof REQUEST_RECORD_PERMISSIONS)[number][0]

export type RequestRecordPermissions = Record<RequestRecordPermissionKey, boolean>

/** The answer for a caller with no access, and the seed every result is built from. */
export const NO_REQUEST_RECORD_PERMISSIONS: RequestRecordPermissions = {
  canView: false,
  canPerformBaseTasks: false,
  canPerformAdminTasks: false,
  canEditMetadata: false,
  canEditPoNumber: false,
  canManageParticipants: false,
  canManageFollowers: false,
  canPause: false,
  canReject: false,
  canDelete: false,
  canEditSubmissions: false,
  canManageDocuments: false,
  canManageAssignees: false,
  canManageWorkflow: false,
  canAdminister: false,
}

export const toRequestRecordPermissions = (allowed: readonly boolean[]): RequestRecordPermissions =>
  zipRecordPermissions('request', REQUEST_RECORD_PERMISSIONS, NO_REQUEST_RECORD_PERMISSIONS, allowed)
